import styles from './move-users-to-new-company-dialog.module.scss';
import { Checkbox, CheckboxLabel, CompanySearch, CreateHeaders, HeaderSizeEnum, IContactType, IUserAccount, OrderTypesEnum, SecondaryButton, SnackBarContext, StepDialog, TextField, textHeader } from '@frontend-workspace/react-shared-lib';
import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { ContactListContext, GetCurrentCompany } from '../customer-context/customer-context';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import WebkuaTable from '../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface MoveUsersToNewCompanyDialogProps {
  users: IUserAccount[];
  onClose: () => void;
  isCreateNewPersons: boolean;
  movedAccounts: { existingEmail: string, newEmail: string }[];
  setIsCreateNewPersons: (isCreateNewPersons: boolean) => void;
  setMovedAccounts: (movedAccounts: { existingEmail: string, newEmail: string }[]) => void;
}

export function MoveUsersToNewCompanyDialog(props: {
  onClose: () => void,
  currentCompany: IContactType
}) {
  const { t } = useTranslation();
  const { data } = useGetRequestNewHook(`companies/${props.currentCompany.number}/userAccounts/forGroup/false/onlyActive/true`, [props.currentCompany]);
  return (
    (data.length > 0 && props.currentCompany ?
      <MoveUsersToNewCompanyDialogContent
        users={data}
        onClose={props.onClose}
        currentCompany={props.currentCompany}
      /> :
      <div> {t("moveUsersToNewCompany.noCompanySelected")}</div>)
  );
};

export function MoveUsersToNewCompanyDialogContent(props: { onClose: () => void, users: IUserAccount[], currentCompany: IContactType }) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const makeRequest = useMakeRequestHook();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [movedAccounts, setMovedAccounts] = useState<{ existingEmail: string, newEmail: string }[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<IContactType | undefined>(undefined);
  const [selectedAccountIds, setselectedAccountIds] = useState<string[]>([]);
  const [isCreateNewPersons, setIsCreateNewPersons] = useState<boolean>(false);
  const selectedAccounts = props.users.filter(u => selectedAccountIds.includes(u.id));

  const handleMoveFinished = () => {
    setSnackbarMessage({ content: t("moveUsersToNewCompany.movingOfUsersFinished"), type: "success" });
    props.onClose();
  };

  UseSignalRHook(handleMoveFinished, [OrderTypesEnum.MoveAccountsToCompanyOrder]);

  useEffect(() => {
    const newMovedAccountEmails = props.users.filter(ua => selectedAccountIds.includes(ua.id)).map(u => {
      return { existingEmail: u.email, newEmail: u.email }
    });
    setMovedAccounts(newMovedAccountEmails);
  }, [selectedAccountIds, props.users]);

  const handleFinish = () => {
    if (!selectedCompany) setSnackbarMessage({ content: t("moveUsersToNewCompany.youMustChooseACompanyToMoveTo"), type: "error" });
    else if (movedAccounts.some(a => !a.newEmail.includes("."))) setSnackbarMessage({ content: "Du må velge et nytt domene", type: "error" });
    else {
      setSnackbarMessage({ content: t("moveUsersToNewCompany.startsMovingOfUsers"), type: "info" });
      const body = {
        movedAccounts,
        createNewPersonsInSO: isCreateNewPersons,
        TargetCompanyNumber: selectedCompany?.number,
        currentCompanyNumber: currentCompany?.number
      }
      makeRequest(`admin/action/moveAccountsToCompany`, "PUT", body);
    }
  };

  return (
    <StepDialog
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      onClose={props.onClose}
      onFinish={handleFinish}
      steps={[t("moveUsersToNewCompany.selectUsersToMove"), t("standard.changeEmail")]}
      title={t("moveUsersToNewCompany.moveUsersToNewCompany")}>
      {activeStep === 0 && <ChooseAccountsStep
        accounts={props.users}
        onClose={props.onClose}
        selectedAccountIds={selectedAccountIds}
        targetCompany={selectedCompany}
        setSelectedAccountIds={setselectedAccountIds}
        onChangeTargetCompany={setSelectedCompany}
        currentCompany={props.currentCompany}
      />}
      {activeStep === 1 && <ChangeEmailStep
        onClose={props.onClose}
        users={selectedAccounts}
        setIsCreateNewPersons={setIsCreateNewPersons}
        isCreateNewPersons={isCreateNewPersons}
        movedAccounts={movedAccounts}
        setMovedAccounts={setMovedAccounts}
      />}
    </StepDialog>
  );
}

function ChooseAccountsStep(props: {
  onClose: () => void,
  onChangeTargetCompany: (companyId: IContactType | undefined) => void,
  targetCompany: IContactType | undefined,
  setSelectedAccountIds: (ids: string[]) => void,
  selectedAccountIds: string[],
  accounts: IUserAccount[],
  currentCompany: IContactType
}) {
  const { t } = useTranslation();
  const contactList = useContext<IContactType[]>(ContactListContext);

  const handleSelectAll = () => props.setSelectedAccountIds(props.accounts.map(ua => ua.id));
  const handleSelectCompany = (companyNumber: string) => props.onChangeTargetCompany(contactList.find(c => c.number === companyNumber))

  const selectAccountRenderer = (params: ValueFormatterParams) => {
    const handleSelectAccount = (e: React.ChangeEvent<Element>, checked: boolean) => {
      if (checked) props.setSelectedAccountIds([...props.selectedAccountIds, params.value]);
      else props.setSelectedAccountIds(props.selectedAccountIds.filter(id => id !== params.value));
    };
    return <Checkbox
      onChange={handleSelectAccount}
      checked={props.selectedAccountIds.includes(params.value)}
    />
  };

  return <>
    <div className='mb-3'>
      <SecondaryButton
        variant='outlined'
        onClick={handleSelectAll}>
        {t("moveUsersToNewCompany.selectAllAccounts")}
      </SecondaryButton>
    </div>
    <Row>
      <Col md='12' lg='8' xl='6' xxl='8'>
        <div className='mb-3'>
          <label>{t("moveUsersToNewCompany.companyYouAreMovingFrom")}</label>
          <TextField
            value={props.currentCompany.name}
            fullWidth
            disabled
          />
        </div>
      </Col>
    </Row>
    <Row>
      <Col md='12' lg='8' xl='6' xxl='8'>
        <div className='mb-3 mb-lg-0'>
          <label>{t("moveUsersToNewCompany.chooseCompanyToMoveTo")}</label>
          <CompanySearch
            companyList={contactList}
            selectedCompanyNumber={props.targetCompany?.number}
            onChange={handleSelectCompany}
          />
        </div>
      </Col>
    </Row>
    <WebkuaTable
      headers={CreateHeaders([
        textHeader("firstName", t("standard.firstName"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("lastName", t("standard.lastName"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("email", t("standard.email"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("id", t("standard.move"), HeaderSizeEnum.SMALL, { renderer: selectAccountRenderer }),
      ])}
      data={props.accounts}
    />
  </>;
}

function ChangeEmailStep(props: MoveUsersToNewCompanyDialogProps) {
  const { t } = useTranslation();
  const domain = props.users[0]?.email.split("@")[1];
  const [newDomain, setNewDomain] = useState<string>(domain);

  const handleNewDomainChange = (event: any) => {
    setNewDomain(event.target.value);
    const updatedAccounts = props.movedAccounts.map(ma => {
      const newEmail = ma.existingEmail.split("@")[0] + "@" + event.target.value;
      ma.newEmail = newEmail;
      return ma;
    });
    props.setMovedAccounts(updatedAccounts);
  };

  const NewEmailRenderer = (params: ValueFormatterParams) => {
    const movedAccount = props.movedAccounts.find(e => e.existingEmail === params.value);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const movedWithoutSelected = props.movedAccounts.filter(ma => ma.existingEmail !== movedAccount?.existingEmail);
      if (movedAccount && e.currentTarget.value !== "") {
        movedAccount.newEmail = e.currentTarget.value;
        const allAccounts = movedWithoutSelected.concat(movedAccount);
        props.setMovedAccounts(allAccounts);
      }
    };
    return (
      <div className='d-flex align-items-center'>
        <TextField
          placeholder={movedAccount?.newEmail}
          onBlur={handleChange}
          fullWidth
        />
      </div>
    )
  };

  return <>
    <Row>
      <Col md='12' lg='8' xl='6' xxl='8'>
        <div className='mb-3 mb-lg-0'>
          <TextField
            value={newDomain}
            label={t("moveUsersToNewCompany.newDomain")}
            onChange={handleNewDomainChange}
            fullWidth
          />
        </div>
      </Col>
    </Row>
    <WebkuaTable
      headers={CreateHeaders([
        textHeader("firstName", t("standard.firstName")),
        textHeader("lastName", t("standard.lastName")),
        textHeader("email", t("standard.email")),
        textHeader("email", t("moveUsersToNewCompany.newEmail"), HeaderSizeEnum.MEDIUM, { renderer: NewEmailRenderer })
      ])}
      data={props.users}
    />
  </>;
}

export default MoveUsersToNewCompanyDialogContent;